import React, { useState, useEffect } from "react";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { RemovePatientButton } from "@/components/staff/RemovePatientDialog";

const label = (c) => c.replace(/_/g, " ");

const Counts = ({ counts }) => (
  <div className="grid sm:grid-cols-3 gap-x-6 gap-y-1 text-xs text-stone-700" data-testid="reset-counts">
    {Object.entries(counts).map(([c, n]) => <div key={c} className="flex justify-between"><span className="capitalize">{label(c)}</span><span className="font-semibold">{n}</span></div>)}
  </div>
);

export const DataResetPanel = ({ token }) => {
  const [patients, setPatients] = useState(null);
  const [keep, setKeep] = useState([]);
  const [q, setQ] = useState("");
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const h = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const [others, setOthers] = useState([]);
  const load = () => {
    fetch(`${API}/admin/data-reset/patients`, { headers: h }).then(r => r.json()).then(d => setPatients(d.patients || []));
    fetch(`${API}/admin/data-reset/other-accounts`, { headers: h }).then(r => r.json()).then(d => setOthers(d.accounts || []));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => { setPreview(null); setKeep(k => k.includes(id) ? k.filter(x => x !== id) : k.length < 2 ? [...k, id] : k); };
  const post = async (path, body) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/admin/data-reset/${path}`, { method: "POST", headers: h, body: JSON.stringify(body) });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || "Failed"); return null; }
      return d;
    } catch { toast.error("Connection error"); return null; } finally { setBusy(false); }
  };
  const runPreview = async () => { const d = await post("preview", { keep_patient_ids: keep }); if (d) setPreview(d); };
  const execute = async () => {
    const d = await post("execute", { keep_patient_ids: keep, confirm });
    if (d) { setResult(d); setPreview(null); setConfirm(""); setKeep([]); toast.success(`Deleted ${d.total} records`); load(); }
  };
  const shown = (patients || []).filter(p => `${p.name || ""} ${p.email || ""}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-4" data-testid="data-reset-tab">
      <Card className="rounded-xl border-red-200 bg-red-50/50 p-4 flex gap-3">
        <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
        <p className="text-sm text-red-800">This permanently deletes every patient and their records (bookings, consultations, meal plans, reports, forms, logs, messages, payments) plus newsletter subscribers, leads and sign-ups. Staff accounts, signatures, the food database and settings are kept. Choose up to two patients to keep.</p>
      </Card>
      <Card className="rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-semibold text-stone-900">Patients to keep ({keep.length}/2)</p>
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name or email" className="max-w-xs rounded-lg h-8" data-testid="reset-search" />
        </div>
        {!patients ? <Loader2 className="w-5 h-5 animate-spin text-stone-400" /> : (
          <div className="max-h-72 overflow-y-auto divide-y divide-stone-100 border border-stone-100 rounded-lg">
            {shown.map(p => (
              <label key={p.user_id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-stone-50" data-testid={`reset-patient-${p.user_id}`}>
                <Checkbox checked={keep.includes(p.user_id)} disabled={!keep.includes(p.user_id) && keep.length >= 2} onCheckedChange={() => toggle(p.user_id)} />
                <span className="font-medium text-stone-900">{p.name || "Unnamed"}</span><span className="text-stone-500">{p.email}</span>
              </label>
            ))}
            {!shown.length && <p className="px-3 py-4 text-sm text-stone-500">No patients found.</p>}
          </div>
        )}
        <Button onClick={runPreview} disabled={busy} variant="outline" className="rounded-lg" data-testid="reset-preview-btn">{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Preview what will be deleted</Button>
      </Card>
      {preview && (
        <Card className="rounded-xl p-4 space-y-3 border-red-200" data-testid="reset-preview">
          <p className="text-sm text-stone-800">Keeping: <b>{preview.kept.map(k => k.email).join(", ") || "no patients"}</b>. <b>{preview.total}</b> records will be deleted:</p>
          <Counts counts={preview.counts} />
          <p className="text-xs text-stone-600">Type <b>{preview.confirm_phrase}</b> to confirm.</p>
          <div className="flex gap-2">
            <Input value={confirm} onChange={e => setConfirm(e.target.value)} className="max-w-xs rounded-lg" data-testid="reset-confirm-input" />
            <Button onClick={execute} disabled={busy || confirm !== preview.confirm_phrase} className="bg-red-600 hover:bg-red-700 text-white rounded-lg" data-testid="reset-execute-btn"><Trash2 className="w-4 h-4 mr-1" />Delete permanently</Button>
          </div>
        </Card>
      )}
      <Card className="rounded-xl p-4 space-y-2" data-testid="old-accounts">
        <p className="text-sm font-semibold text-stone-900">Old accounts from the previous build ({others.length})</p>
        <p className="text-xs text-stone-500">Dietitian and partner logins created before the single dashboard. Admin accounts are never listed here.</p>
        {others.length ? (
          <div className="divide-y divide-stone-100 border border-stone-100 rounded-lg">
            {others.map(a => (
              <div key={a.user_id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`old-account-${a.user_id}`}>
                <span className="font-medium text-stone-900">{a.name || "Unnamed"}</span><span className="text-stone-500">{a.email}</span>
                <span className="text-[11px] text-stone-400 capitalize">{(a.role || "").replace(/_/g, " ")}</span>
                <div className="ml-auto"><RemovePatientButton patient={a} token={token} label="Remove" onRemoved={load} /></div>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-stone-500">None left.</p>}
      </Card>
      {result && (
        <Card className="rounded-xl p-4 space-y-2 border-green-200 bg-green-50/40" data-testid="reset-result">
          <p className="text-sm text-green-800">Done. Deleted {result.total} records. Kept: {result.kept.map(k => k.email).join(", ") || "none"}.</p>
          <Counts counts={result.deleted} />
        </Card>
      )}
    </div>
  );
};
